import { FC } from "react";
import { cn } from "@/lib/utils";
import { ProgramStructure2Data } from "./_schema";
import { ArrowDownIcon } from "./_arrow";
import RevealOnScroll from "@/components/atoms/RevealOnScroll";

const SubjectRow: FC<{
    subject: any;
    index: number;
    categoryItem: any;
    expandedElective: number | null;
    onElectiveToggle: (id: number | null) => void;
}> = ({ subject, index, categoryItem, expandedElective, onElectiveToggle }) => {
    const isElective = subject.is_elective === 1;
    const defaultBgColor = subject.background_color || "rgba(255,255,255,0.1)";
    const hoverBgColor = "rgba(2,167,182,0.3)";
    
    return (
        <div 
            key={subject.id || index}
            className={cn(
                "flex w-full transition-colors duration-200 cursor-pointer",
                "gap-[8px] px-[16px] py-[10px]",
                "sm:gap-[15px] sm:px-[18px] sm:py-[12px]",
                "md:gap-[20px] md:px-[24px] md:py-[16px]",
                "lg:gap-[20px] lg:px-[24px] lg:py-[16px]",
                "xl:gap-[20px] xl:px-[24px] xl:py-[16px]",
                "items-start"
            )}
            style={{ 
                backgroundColor: defaultBgColor,
                '--hover-bg-color': hoverBgColor
            } as React.CSSProperties & { '--hover-bg-color': string }}
            onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = hoverBgColor;
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = defaultBgColor;
            }}
        >
            {/* Type column - only on md+ */}
            <p className={cn(
                "font-medium leading-normal shrink-0 wrap-break-word",
                "xs:text-[14px] xs:w-[64px]",
                "sm:text-[13px] sm:w-[60px]",
                "md:text-[16px] md:w-[60px]",
                "lg:text-[16px] lg:w-[60px]",
                "xl:text-[16px] xl:w-[60px]"
            )}
            style={{
                color: subject.text_color || "#FFFFFF",
                fontFamily: "var(--font-google-sans)"
            }}>
                {subject.type || "-"}
            </p>
            
            {/* Divider after Type - only on md+ */}
            <div className="flex items-center justify-center lg:flex lg:items-center lg:justify-center xl:flex xl:items-center xl:justify-center"
                 style={{ height: '22px' }}>
                <div className={cn(
                    isElective ? "h-0 w-[4px]" : "w-[0.6px] h-[22px]"
                )}
                style={{ backgroundColor: "#FFFFFF" }} />
            </div>
            
            {/* Arrow for electives */}
            {isElective && (
                <button
                    type="button"
                    data-cta-name="elective_expand"
                    title="Expand Electives"
                    onClick={() => onElectiveToggle(expandedElective === subject.id ? null : (subject.id as number))}
                    className={cn(
                        "shrink-0",
                        "sm:mr-[2px]",
                        "md:mr-[4px]",
                        "lg:mr-[4px]",
                        "xl:mr-[4px]"
                    )}
                >
                    <ArrowDownIcon className={cn(
                        "transition-transform text-white",
                        "size-[14px]",
                        "sm:size-[15px]",
                        "md:size-[20px]",
                        "lg:size-[20px]",
                        "xl:size-[20px]",
                        expandedElective === subject.id ? "" : "-rotate-90"
                    )} />
                </button>
            )}
            
            {/* Subject name and electives list */}
            <div className={cn(
                "flex-1 flex flex-col",
                isElective ? "gap-[12px]" : ""
            )}>
                <p className={cn(
                    "font-medium leading-normal",
                    "text-[14px]",
                    "sm:text-[12px]",
                    "md:text-[16px]",
                    "lg:text-[16px]",
                    "xl:text-[16px]"
                )}
                style={{
                    color: subject.text_color || "#FFFFFF",
                    fontFamily: "var(--font-google-sans)"
                }}>
                    {subject.name}
                </p>
                
                {/* Electives list */}
                {isElective && expandedElective === subject.id && subject.electives && subject.electives.length > 0 && (
                    <ol className={cn(
                        "list-decimal ml-[18px] flex flex-col",
                        "gap-[12px] text-[12px]",
                        "sm:gap-[10px] sm:text-[10px] sm:ml-[15px]",
                        "md:gap-[12px] md:text-[14px] md:ml-[21px]",
                        "lg:gap-[14px] lg:text-[14px] lg:ml-[21px]",
                        "xl:gap-[14px] xl:text-[14px] xl:ml-[21px]"
                    )}
                    style={{
                        fontFamily: "var(--font-google-sans)",
                        fontStyle: "italic",
                        fontWeight: 400
                    }}>
                        {subject.electives.map((elective: any, idx: number) => (
                            <li 
                                key={idx}
                                style={{ 
                                    color: elective.text_color || "#FFFFFF",
                                    lineHeight: "normal"
                                }}
                            >
                                {elective.name}
                            </li>
                        ))}
                    </ol>
                )}
            </div>
            
            {/* Divider before Credits */}
            <div className={cn(
                "flex items-center justify-center shrink-0 self-start",
                isElective ? "h-[22px]" : ""
            )}>
                <div className={cn(
                    isElective ? "h-0 w-[22px]" : "w-[0.6px]",
                    !isElective && "h-[17px]",
                    !isElective && "sm:h-[16px]",
                    !isElective && "md:h-[22px]",
                    !isElective && "lg:h-[22px]",
                    !isElective && "xl:h-[22px]"
                )}
                style={{ backgroundColor: "#FFFFFF" }} />
            </div>
            
            {/* Credits */}
            <p className={cn(
                "font-medium leading-normal shrink-0 text-center",
                isElective && "self-start",
                "text-[14px] w-[53px]",
                "sm:text-[13px] sm:w-[45px]",
                "md:text-[18px] md:w-[60px]",
                "lg:text-[18px] lg:w-[60px]",
                "xl:text-[18px] xl:w-[60px]"
            )}
            style={{
                color: subject.text_color || "#FFFFFF",
                fontFamily: "var(--font-google-sans)"
            }}>
                {subject.credits || ""}
            </p>
        </div>
    );
};

// Subjects Table Component
export const SubjectsTable: FC<{
    parsedData: ProgramStructure2Data;
    subjects: any[];
    activeCategories: any[];
    activeSemester: any;
    expandedElective: number | null;
    onElectiveToggle: (id: number | null) => void;
    totalCredits: number;
    // Reveal animation is suppressed by the parent once the user switches semester/
    // category, so the table swaps content instantly instead of re-animating each time.
    animate?: boolean;
}> = ({ parsedData, subjects, activeCategories, activeSemester, expandedElective, onElectiveToggle, totalCredits, animate }) => {
    // Get total credits text - use semester-level if available, fallback to global
    const totalCreditsText = activeSemester?.total_credits_text || parsedData.table_total_text;

    // Get total credits text color - use semester-level if available, fallback to global
    const totalCreditsTextColor = activeSemester?.total_credits_text_color || parsedData.table_total_text_color || "#FFFFFF";

    const isAnimationEnabled =
        animate ??
        (parsedData.enable_animation === true || parsedData.enable_animation === 1);

    return (
    <div className={cn(
        "flex flex-col w-full",
        "gap-[2px]",
        "sm:gap-[1.5px] sm:flex-1",
        "md:gap-[2px] md:flex-1",
        "lg:gap-[2px] lg:flex-1",
        "xl:gap-[2px] xl:flex-1"
    )}>
        {/* Table Header */}
        <RevealOnScroll once={true} y={-50} x={0} delay={100} enableAnimation={isAnimationEnabled}>
        <div className={cn(
            "flex items-center justify-center w-full border-t-[0.6px] border-b-[0.6px] border-white",
            "gap-[8px] px-[16px] py-[10px]",
            "sm:gap-[15px] sm:px-[18px] sm:py-[12px]",
            "md:gap-[20px] md:px-[24px] md:py-[16px]",
            "lg:gap-[20px] lg:px-[24px] lg:py-[16px]",
            "xl:gap-[20px] xl:px-[24px] xl:py-[16px]"
        )}>
            <p className={cn(
                "font-bold leading-[1.2] shrink-0",
                "text-[16px] tracking-[0.48px] xs:w-[64px]",
                "sm:text-[13px] sm:tracking-[0.39px] md:w-[60px]",
                "md:text-[18px] md:tracking-[0.54px] md:w-[60px]",
                "lg:text-[18px] lg:tracking-[0.54px] lg:w-[60px]",
                "xl:text-[18px] xl:tracking-[0.54px] xl:w-[60px]"
            )}
            style={{
                color: parsedData.table_heading_1_color || "#FFFFFF",
                fontFamily: "var(--font-merriweather)"
            }}>
                {parsedData.table_heading_1}
            </p>
            
            <div className="hidden md:flex md:h-full md:items-center md:justify-center lg:flex lg:h-full lg:items-center lg:justify-center xl:flex xl:h-full xl:items-center xl:justify-center">
                <div className={cn(
                    "md:h-[22px] md:w-[0.6px]",
                    "lg:h-[22px] lg:w-[0.6px]",
                    "xl:h-[22px] xl:w-[0.6px]"
                )}
                style={{ backgroundColor: "#FFFFFF" }} />
            </div>
            
            <p className={cn(
                "flex-1 font-bold leading-[1.2]",
                "text-[16px] tracking-[0.48px]",
                "sm:text-[13px] sm:tracking-[0.39px]",
                "md:text-[18px] md:tracking-[0.54px]",
                "lg:text-[18px] lg:tracking-[0.54px]",
                "xl:text-[18px] xl:tracking-[0.54px]"
            )}
            style={{
                color: parsedData.table_heading_2_color || "#FFFFFF",
                fontFamily: "var(--font-merriweather)"
            }}>
                {parsedData.table_heading_2}
            </p>
            
            <div className="flex h-full items-center justify-center">
                <div className={cn(
                    "h-[19px] w-[0.6px]",
                    "sm:h-[16px]",
                    "md:h-[22px]",
                    "lg:h-[22px]",
                    "xl:h-[22px]"
                )}
                style={{ backgroundColor: "#FFFFFF" }} />
            </div>
            
            <p className={cn(
                "font-bold leading-[1.2] shrink-0",
                "text-[16px] tracking-[0.48px] w-[53px]",
                "sm:text-[13px] sm:tracking-[0.39px] sm:w-[45px]",
                "md:text-[18px] md:tracking-[0.54px] md:w-[60px]",
                "lg:text-[18px] lg:tracking-[0.54px] lg:w-[60px]",
                "xl:text-[18px] xl:tracking-[0.54px] xl:w-[60px]"
            )}
            style={{
                color: parsedData.table_heading_3_color || "#FFFFFF",
                fontFamily: "var(--font-merriweather)"
            }}>
                {parsedData.table_heading_3}
            </p>
        </div>
        </RevealOnScroll>
        
        {/* Table rows */}
        {subjects.map((subject, index) => {
            // Find the category item by first finding the correct category, then the item within it
            let categoryItem = null;
            if (subject.category_id && subject.category_item_id) {
                const category = activeCategories.find(cat => cat.id === subject.category_id);
                if (category && category.items) {
                    categoryItem = category.items.find((item: any) => item.id === subject.category_item_id);
                }
            }
            
            return (
                <RevealOnScroll once={true} key={subject.id || index} y={-100 * (index + 1)}  delay={index * 100} enableAnimation={isAnimationEnabled}>
                <SubjectRow
                    key={subject.id || index}
                    subject={subject}
                    index={index}
                    categoryItem={categoryItem}
                    expandedElective={expandedElective}
                    onElectiveToggle={onElectiveToggle}
                />
                </RevealOnScroll>
            );
        })}
        
        {/* Table Total Row - Conditionally rendered based on semester setting */}

<RevealOnScroll once={true} y={-100 * (subjects.length + 1)} delay={subjects.length * 100} enableAnimation={isAnimationEnabled}>
            <div className={cn(
                "flex items-center justify-center w-full border-t-[0.6px] border-b-[0.6px] border-white",
                "gap-[8px] px-[16px] py-[10px]",
                "sm:gap-[15px] sm:px-[18px] sm:py-[12px]",
                "md:gap-[20px] md:px-[24px] md:py-[16px]",
                "lg:gap-[20px] lg:px-[24px] lg:py-[16px]",
                "xl:gap-[20px] xl:px-[24px] xl:py-[16px]"
            )}>
                <p className={cn(
                    "flex-1 font-medium leading-normal",
                    "text-[14px]",
                    "sm:text-[13.5px]",
                    "md:text-[18px]",
                    "lg:text-[18px]",
                    "xl:text-[18px]"
                )}
                style={{
                    color: totalCreditsTextColor,
                    fontFamily: "var(--font-google-sans)"
                }}>
                    {totalCreditsText}
                </p>
                
                <div className="flex h-full items-center justify-center">
                    <div className={cn(
                        "h-[17px] w-[0.6px]",
                        "sm:h-[16px]",
                        "md:h-[22px]",
                        "lg:h-[22px]",
                        "xl:h-[22px]"
                    )}
                    style={{ backgroundColor: "#FFFFFF" }} />
                </div>
                
                <p className={cn(
                    "font-medium leading-normal shrink-0 text-center",
                    "text-[14px] w-[60px]",
                    "sm:text-[13.5px] sm:w-[45px]",
                    "md:text-[18px] md:w-[60px]",
                    "lg:text-[18px] lg:w-[60px]",
                    "xl:text-[18px] xl:w-[60px]"
                )}
                style={{
                    color: totalCreditsTextColor,
                    fontFamily: "var(--font-google-sans)"
                }}>
                    {totalCredits}
                </p>
            </div>
            </RevealOnScroll>
        
        {/* Disclaimer */}
        {parsedData.note && (
            <RevealOnScroll once={true} y={-100} x={0} delay={100 * (subjects.length + 1)} enableAnimation={isAnimationEnabled}>
            <p className={cn(
                "font-normal italic leading-[1.49] mt-[8px]",
                "text-[12px]",
                "sm:text-[10.5px] sm:mt-[6px]",
                "md:text-[12px] md:mt-[8px]",
                "lg:text-[14px] lg:mt-[8px]",
                "xl:text-[14px] xl:mt-[8px]"
            )}
            style={{
                color: parsedData.note_color || "#FFFFFF",
                fontFamily: "var(--font-google-sans)"
            }}>
                {parsedData.note}
            </p>
            </RevealOnScroll>
        )}
    </div>
    );
};